/**
 * Drafts prove themselves against the server (BUG-065, hardened in BUG-075).
 *
 * A capture leaves captureDrafts only when the CLIENT processes the server's
 * confirm. Anything that interrupts that window — an update reload, iOS
 * reclaiming the PWA, a failed IndexedDB transaction — leaves a moment that
 * DELIVERED sitting on disk as an "unsent" draft, indistinguishable from one
 * that never made it.
 *
 * The check rides Wave A's idempotency keys: every send stamps its draft with
 * the exact keys it's going out under, so we can ask the server which of them
 * landed and quietly clear the ones that did. A draft whose keys all landed is
 * delivered; anything else stays put — resending is duplicate-proof by design,
 * so the safe rule is also the simple one.
 *
 * BUG-075 changed WHEN this runs. It used to be latched to one pass per launch
 * over drafts that already existed at boot — so a draft the current session
 * left behind could only heal on the next cold start, which on a phone that
 * stays open all day is never. It is now throttled rather than latched, it
 * considers any draft stamped as sending longer ago than a send should take,
 * and it runs whenever the user could be looking at a draft surface:
 *
 *   - at launch, after auth (App.tsx)
 *   - when the app returns to the foreground (App.tsx)
 *   - when a draft surface shows a non-zero count (CameraSection)
 *
 * Drafts written before stamping shipped are asked about by capture id instead
 * — every key derives from it, so a prefix match still answers "did this
 * capture reach anyone". That's coarser (a partly-failed multi-send would clear
 * too), which is why it exists only for the legacy rows that can't answer any
 * better.
 */
import { api } from '../api/client';
import { drafts, DRAFT_MAX_AGE_MS } from './media/drafts';
import { sendEngine } from './queue/engine';

const MAX_KEYS = 100;
/** Floor between passes: the triggers are cheap and frequent, the round trip
 * shouldn't be. */
const MIN_INTERVAL_MS = 20_000;

let lastRun = 0;
let inFlight: Promise<number> | null = null;

/**
 * Ask the server which leftover drafts already landed and clear them, then age
 * out captures nobody ever sent. Never throws, never blocks a render, and
 * coalesces concurrent callers into one pass.
 */
export function reconcileSentDrafts(
  userId: number,
  opts?: { force?: boolean }
): Promise<number> {
  if (inFlight) return inFlight;
  const now = Date.now();
  if (!opts?.force && now - lastRun < MIN_INTERVAL_MS) return Promise.resolve(0);
  // The window is burnt by the pass that COMPLETES it, inside run() — stamping
  // it here meant an offline pass locked the next twenty seconds out of trying
  // again, so a phantom could outlive a whole stretch of triggers on the
  // strength of one failed round trip (BUG-097).
  inFlight = run(userId).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function run(userId: number): Promise<number> {
  let cleared = 0;

  try {
    const candidates = await drafts.stampedCandidates(userId);
    // A draft the queue is still delivering is not a phantom — it's about to be
    // deleted by the send itself. The ledger has to be LOADED before it can be
    // asked who it holds (BUG-122): at launch this runs right behind auth, and
    // an empty pre-boot mirror would put every queued send on the ask list.
    await sendEngine.whenReady();
    const held = sendEngine.activeMediaIds();
    const asking = candidates.filter((c) => !held.has(c.id));

    // The ask is built a WHOLE candidate at a time, and only the candidates
    // that fit are judged from the answer. Flattening first and truncating
    // after meant a draft past the cut was never asked about yet still read as
    // "not landed" — a delivered moment that could never heal, pass after
    // pass, because the truncation lands in the same place every time. The
    // ones left over simply wait for the next pass.
    const askedKeys = new Set<string>();
    // Drafts from before stamping existed can still be asked about by capture
    // id: every key derives from it. Prefix answers "reached anyone", which is
    // all the old rows can know.
    const captureIds: string[] = [];
    const asked: typeof asking = [];
    for (const candidate of asking) {
      if (candidate.keys.length === 0) {
        if (captureIds.length >= MAX_KEYS) continue;
        captureIds.push(candidate.id);
      } else {
        if (askedKeys.size + candidate.keys.length > MAX_KEYS) continue;
        for (const key of candidate.keys) askedKeys.add(key);
      }
      asked.push(candidate);
    }
    const keys = [...askedKeys];

    if (keys.length > 0 || captureIds.length > 0) {
      const response = await api.post<{
        landed: string[];
        landed_captures?: string[];
      }>('/messages/sent-keys', { client_keys: keys, capture_ids: captureIds });
      const landed = new Set(response.data?.landed ?? []);
      const landedCaptures = new Set(response.data?.landed_captures ?? []);

      const delivered = asked
        .filter((c) =>
          c.keys.length > 0
            ? c.keys.every((key) => landed.has(key))
            : landedCaptures.has(c.id)
        )
        .map((c) => c.id);

      if (delivered.length > 0) {
        await drafts.remove(delivered, 'delivered');
        console.log(`[Reconcile] ${delivered.length} draft(s) had already landed`);
        cleared = delivered.length;
      }
      // A queued row for a landed key would re-send something the server has
      if (landed.size > 0) await sendEngine.dropByClientKeys([...landed]);
    }
    // Reached only by a pass that got its answer (or had nothing to ask):
    // THIS is what spends the throttle window.
    lastRun = Date.now();
  } catch (err) {
    // Offline or the endpoint is unreachable — the drafts simply stay, which is
    // exactly the old behavior. Never block the launch on this.
    console.warn('[Reconcile] skipped:', err);
  }

  // Independent of the server: captures that were never even dispatched stop
  // waiting after a week. Runs AFTER the ask, so anything that actually landed
  // is removed as delivered rather than aged out.
  try {
    const swept = await drafts.sweepExpired(userId, DRAFT_MAX_AGE_MS);
    if (swept > 0) console.log(`[Reconcile] ${swept} draft(s) aged out`);
  } catch (err) {
    console.warn('[Reconcile] sweep skipped:', err);
  }

  return cleared;
}

/** Tests only. */
export function resetReconcileLatch(): void {
  lastRun = 0;
  inFlight = null;
}
